import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import { pdfjs } from 'react-pdf';
import { ChevronLeft, ChevronRight, Download, Maximize2, Minimize2, X } from 'lucide-react';
import pdfWorkerSource from 'pdfjs-dist/build/pdf.worker.min.mjs?raw';

// Keep the PDF worker inside the main application bundle. The production
// deploy currently omits static/media, so a separate worker file is replaced
// by index.html. A Blob URL removes that deployment dependency entirely.
const pdfWorkerBlob = new Blob([pdfWorkerSource], { type: 'text/javascript' });
pdfjs.GlobalWorkerOptions.workerSrc = URL.createObjectURL(pdfWorkerBlob);

const FlipPage = forwardRef(function FlipPage({ pageNumber, imageUrl }, ref) {
  return (
    <div ref={ref} className="catalog-flip-page">
      <img src={imageUrl} alt={`صفحه ${pageNumber.toLocaleString('fa-IR')}`} draggable="false" />
    </div>
  );
});

export default function CatalogFlipbookModal({ catalog, pdfUrl, downloadUrl, onClose }) {
  const flipBookRef = useRef(null);
  const modalRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [loadError, setLoadError] = useState('');
  const [pageImages, setPageImages] = useState([]);
  const [renderedPages, setRenderedPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageInput, setPageInput] = useState('1');
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  const isMobile = viewportWidth < 640;
  const renderWidth = isMobile
    ? Math.max(250, Math.min(viewportWidth - 38, 360))
    : Math.max(380, Math.min(Math.floor((viewportWidth - 150) / 2), 560));
  const pageHeight = Math.round(renderWidth * 1.414);

  const pages = useMemo(() => pageImages.map((imageUrl, index) => ({ imageUrl, pageNumber: index + 1 })), [pageImages]);

  useEffect(() => {
    let cancelled = false;
    let loadingTask;
    let pdfDocument;
    const objectUrls = [];

    setIsLoading(true);
    setLoadError('');
    setNumPages(0);
    setRenderedPages(0);
    setPageImages([]);
    setPageIndex(0);

    async function renderPdfPages() {
      try {
        loadingTask = pdfjs.getDocument({ url: pdfUrl });
        pdfDocument = await loadingTask.promise;
        if (cancelled) return;
        setNumPages(pdfDocument.numPages);

        const rendered = [];
        for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
          if (cancelled) return;
          const pdfPage = await pdfDocument.getPage(pageNumber);
          const originalViewport = pdfPage.getViewport({ scale: 1 });
          const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
          const scale = (renderWidth * pixelRatio) / originalViewport.width;
          const viewport = pdfPage.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d', { alpha: false });
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          await pdfPage.render({ canvasContext: context, viewport }).promise;

          const blob = await new Promise((resolve, reject) => {
            canvas.toBlob((value) => (value ? resolve(value) : reject(new Error('Page image creation failed'))), 'image/png');
          });
          const objectUrl = URL.createObjectURL(blob);
          objectUrls.push(objectUrl);
          rendered.push(objectUrl);
          if (!cancelled) setRenderedPages(pageNumber);
          pdfPage.cleanup();
        }

        if (!cancelled) {
          setPageImages(rendered);
          setIsLoading(false);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Catalog PDF load error:', error);
        setLoadError('فایل کاتالوگ بارگذاری نشد. لطفاً دوباره تلاش کنید.');
        setIsLoading(false);
      }
    }

    renderPdfPages();
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      loadingTask?.destroy?.();
      pdfDocument?.destroy?.();
    };
  }, [pdfUrl, renderWidth]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    const handleFullscreen = () => setIsFullscreen(document.fullscreenElement === modalRef.current);
    window.addEventListener('resize', handleResize);
    document.addEventListener('fullscreenchange', handleFullscreen);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('fullscreenchange', handleFullscreen);
    };
  }, []);

  useEffect(() => {
    setPageInput(String(pageIndex + 1));
  }, [pageIndex]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !document.fullscreenElement) onClose();
      if (event.key === 'ArrowLeft') flipBookRef.current?.pageFlip()?.flipNext();
      if (event.key === 'ArrowRight') flipBookRef.current?.pageFlip()?.flipPrev();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await modalRef.current?.requestFullscreen?.();
    } catch {
      // Fullscreen is optional; the reader remains usable if the browser blocks it.
    }
  }

  function commitPageInput() {
    const normalized = String(pageInput || '')
      .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
      .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
    const nextPage = Math.min(Math.max(Number.parseInt(normalized, 10) || 1, 1), numPages || 1);
    setPageInput(String(nextPage));
    flipBookRef.current?.pageFlip()?.flip(nextPage - 1);
  }

  return (
    <div className="catalog-modal-backdrop" onMouseDown={onClose}>
      <div ref={modalRef} className="catalog-modal" onMouseDown={(event) => event.stopPropagation()} dir="rtl">
        <header className="catalog-modal-header">
          <div className="min-w-0">
            <div className="truncate text-sm font-extrabold text-slate-900">{catalog.title}</div>
            <div className="mt-1 text-xs text-slate-500">
              {numPages ? `${numPages.toLocaleString('fa-IR')} صفحه` : 'کاتالوگ PDF'}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <a href={downloadUrl} className="catalog-control catalog-download">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">دانلود</span>
            </a>
            <button type="button" onClick={toggleFullscreen} className="catalog-control" aria-label="تمام صفحه">
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button type="button" onClick={onClose} className="catalog-control" aria-label="بستن">
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="catalog-reader">
          {isLoading ? (
            <div className="catalog-document-state">
              <span className="catalog-spinner" />
              <span>
                در حال آماده‌سازی کاتالوگ...
                {numPages > 0 ? ` (${renderedPages.toLocaleString('fa-IR')} از ${numPages.toLocaleString('fa-IR')})` : ''}
              </span>
            </div>
          ) : loadError ? (
            <div className="catalog-document-state text-red-600">{loadError}</div>
          ) : pages.length ? (
            <HTMLFlipBook
                key={`${catalog.id}-${isMobile ? 'mobile' : 'desktop'}-${renderWidth}`}
                ref={flipBookRef}
                width={renderWidth}
                height={pageHeight}
                size="fixed"
                minWidth={250}
                maxWidth={560}
                minHeight={354}
                maxHeight={792}
                showCover
                usePortrait={isMobile}
                mobileScrollSupport
                maxShadowOpacity={0.35}
                flippingTime={700}
                drawShadow
                clickEventForward
                useMouseEvents
                swipeDistance={25}
                onFlip={(event) => setPageIndex(event.data)}
                className="catalog-flipbook"
              >
                {pages.map(({ pageNumber, imageUrl }) => (
                  <FlipPage
                    key={pageNumber}
                    pageNumber={pageNumber}
                    imageUrl={imageUrl}
                  />
                ))}
              </HTMLFlipBook>
          ) : null}
        </div>

        {numPages > 0 && (
          <footer className="catalog-modal-footer" dir="ltr">
            <button type="button" className="catalog-nav-button" onClick={() => flipBookRef.current?.pageFlip()?.flipPrev()} disabled={pageIndex <= 0} aria-label="صفحه قبل">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex min-w-32 items-center justify-center gap-2 text-xs font-bold text-slate-700" dir="rtl">
              <span>صفحه</span>
              <input
                value={pageInput}
                onChange={(event) => setPageInput(event.target.value.replace(/[^\d۰-۹٠-٩]/g, ''))}
                onBlur={commitPageInput}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    commitPageInput();
                  }
                }}
                className="h-8 w-14 rounded-xl border border-slate-200 bg-white px-2 text-center text-xs font-black text-slate-800 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
                inputMode="numeric"
                aria-label="شماره صفحه"
              />
              <span>از {numPages.toLocaleString('fa-IR')}</span>
            </div>
            <button type="button" className="catalog-nav-button" onClick={() => flipBookRef.current?.pageFlip()?.flipNext()} disabled={pageIndex >= numPages - 1} aria-label="صفحه بعد">
              <ChevronRight className="h-5 w-5" />
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
